import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Font_Style = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">✍️ CSS Font Styling</h1>

      {/* Intro (Gradient Box - Fixed Style) */}
      <div
        style={{
          background: "linear-gradient(135deg, #d1fae5, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#166534", marginBottom: "0.5rem" }}>
          ✨ What is Font Styling?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          CSS provides several properties to control the appearance of text,
          including <code>font-style</code>, <code>font-weight</code>, and{" "}
          <code>font-variant</code>. These help you emphasize, highlight, or
          transform your text to improve readability and design.
        </p>
      </div>

      {/* Font Style */}
      <div className="tutorial-section">
        <h2 className="section-title">🎨 CSS Font Style</h2>
        <p>
          The <code>font-style</code> property specifies how text should be
          displayed — normally, italicized, or oblique.
        </p>

        <ul className="tutorial-list">
          <li>
            <strong>normal</strong> – Default style, regular text.
          </li>
          <li>
            <strong>italic</strong> – Text is slanted using special italic
            letterforms.
          </li>
          <li>
            <strong>oblique</strong> – Text is slanted artificially (similar to
            italic but less refined).
          </li>
        </ul>

        <CodeCopyBlock
          code={`p.normal { font-style: normal; }
p.italic { font-style: italic; }
p.oblique { font-style: oblique; }`}
        />

        <p style={{ fontStyle: "normal" }}>This is normal text.</p>
        <p style={{ fontStyle: "italic" }}>This is italic text.</p>
        <p style={{ fontStyle: "oblique" }}>This is oblique text.</p>
      </div>

      {/* Font Weight */}
      <div className="tutorial-section">
        <h2 className="section-title">💪 CSS Font Weight</h2>
        <p>
          The <code>font-weight</code> property controls how <b>thick</b> or{" "}
          <b>thin</b> text characters appear.
        </p>

        <ul className="tutorial-list">
          <li>
            <strong>normal</strong> – Regular text (same as <code>400</code>).
          </li>
          <li>
            <strong>bold</strong> – Thicker characters (same as <code>700</code>
            ).
          </li>
          <li>
            <strong>bolder</strong> – Even bolder text than the parent element.
          </li>
          <li>
            <strong>lighter</strong> – Lighter text than the parent element.
          </li>
          <li>
            <strong>100–900</strong> – Numeric values where 100 is thin and 900
            is extra bold.
          </li>
        </ul>

        <CodeCopyBlock
          code={`p.normal { font-weight: normal; }
p.bold { font-weight: bold; }
p.light { font-weight: lighter; }
p.custom { font-weight: 900; }`}
        />

        <p style={{ fontWeight: "normal" }}>Normal (400)</p>
        <p style={{ fontWeight: "bold" }}>Bold (700)</p>
        <p style={{ fontWeight: "lighter" }}>Lighter</p>
        <p style={{ fontWeight: 900 }}>Extra Bold (900)</p>
      </div>

      {/* Font Variant */}
      <div className="tutorial-section">
        <h2 className="section-title">🔡 CSS Font Variant</h2>
        <p>
          The <code>font-variant</code> property is used to display text in{" "}
          <b>small-caps</b>. This means lowercase letters are shown as smaller
          uppercase letters, while real uppercase letters remain the same size.
        </p>

        <ul className="tutorial-list">
          <li>
            <strong>normal</strong> – Default, no effect.
          </li>
          <li>
            <strong>small-caps</strong> – Converts lowercase to uppercase in
            smaller size.
          </li>
        </ul>

        <CodeCopyBlock
          code={`p.normal { font-variant: normal; }
p.small { font-variant: small-caps; }`}
        />

        <p style={{ fontVariant: "normal" }}>This is normal text.</p>
        <p style={{ fontVariant: "small-caps" }}>
          This is small-caps text example.
        </p>
      </div>

      {/* Notes */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Notes</h2>
        <ul className="tutorial-list">
          <li>
            Not all fonts support every <code>font-weight</code> value (many
            only support <code>normal</code> and <code>bold</code>).
          </li>
          <li>
            <code>italic</code> is preferred over <code>oblique</code>, since it
            uses special designed characters.
          </li>
          <li>
            <code>font-variant: small-caps</code> is great for headings and
            stylistic text.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know <code>font-style</code>, <code>font-weight</code>,
          and <code>font-variant</code>, move on to{" "}
          <a href="/css/font-size" className="learn-more">
            CSS Font Size
          </a>{" "}
          to control how large or small your text should be.
        </p>
      </div>
    </div>
  );
};

export default Font_Style;
